import Vue from 'vue'
import api from '@/apis/siteEdgePlayback.js'
import parser from '@/models/siteEdgePlayback'
import {
  RESET_STATE,
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  INIT_DEVICE_EVENTS,
  GET_SITE_EDGE_PLAYBACK_REQUEST,
  GET_SITE_EDGE_PLAYBACK_SUCCESS,
  GET_SITE_EDGE_PLAYBACK_FAILURE,
  GET_DEVICE_EDGE_PLAYBACK_REQUEST,
  GET_DEVICE_EDGE_PLAYBACK_SUCCESS,
  GET_DEVICE_EDGE_PLAYBACK_FAILURE,
} from '@/store/mutation-types'

let initialState = {
  siteEdgePlaybacks: [],
  deviceEdgePlaybacks: {},
  status: {
    getSiteEdgePlaybacks: null,
    getDeviceEdgePlaybacks: null,
  },
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  siteEdgePlaybacks: function (state) {
    return state.siteEdgePlaybacks
  },
  deviceEdgePlaybacks: function () {
    return state.deviceEdgePlaybacks
  },
  statusGetSiteEdgePlaybacks: function (state) {
    return state.status.getSiteEdgePlaybacks
  },
}

// actions
const actions = {
  getSiteEdgePlaybacks: function ({commit, state}, {siteId, targetRecordId}) {
    commit(GET_SITE_EDGE_PLAYBACK_REQUEST, {siteId})

    return new Promise((resolve, reject) => {
      api.getSiteEdgePlaybacks({siteId, targetRecordId}).then(
        res => {
          let data = res.body
          commit(GET_SITE_EDGE_PLAYBACK_SUCCESS, {data})
          resolve(state.siteEdgePlaybacks)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_EDGE_PLAYBACK_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  /**
   * 클립 재생용 서명 URL을 발급받는다 (edge_playback/view).
   *
   * device-level 목록 응답에서 url이 제거되어(CHEKT-1406), 재생 시점에 record_id로 발급한다.
   * 별도 state에 저장하지 않고(재생 컴포넌트가 직접 사용) Promise를 url 문자열로 resolve한다.
   * 실패 시 {status, statusText}로 reject하고 PROCESS_API_FAILURE로 전역 에러 상태를 갱신한다.
   *
   * @param {Object} ctx - Vuex 컨텍스트 (commit 사용)
   * @param {Object} payload
   * @param {number|string} payload.siteId   - 사이트 아이디
   * @param {number|string} payload.deviceId - 카메라 디바이스 아이디
   * @param {number|string} payload.recordId - 클립 record_id
   * @returns {Promise<string>} 서명된 재생 URL
   */
  getEdgePlaybackViewUrl: function ({commit}, {siteId, deviceId, recordId}) {
    return new Promise((resolve, reject) => {
      api.getEdgePlaybackViewUrl({siteId, deviceId, recordId}).then(
        res => {
          resolve(res.body?.url)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  /**
   * 클립 다운로드용 서명 URL을 발급받는다 (edge_playback/download).
   *
   * view와 흐름은 동일하나 백엔드 권한(SITE_PARTITION_EDGE_PLAYBACK_DOWNLOAD_ACTION)과
   * 접근 이력(action=downloaded)이 다르다. Promise를 url 문자열로 resolve한다.
   * 실패 시 {status, statusText}로 reject하고 PROCESS_API_FAILURE로 전역 에러 상태를 갱신한다.
   *
   * @param {Object} ctx - Vuex 컨텍스트 (commit 사용)
   * @param {Object} payload
   * @param {number|string} payload.siteId   - 사이트 아이디
   * @param {number|string} payload.deviceId - 카메라 디바이스 아이디
   * @param {number|string} payload.recordId - 클립 record_id
   * @returns {Promise<string>} 서명된 다운로드 URL
   */
  getEdgePlaybackDownloadUrl: function ({commit}, {siteId, deviceId, recordId}) {
    return new Promise((resolve, reject) => {
      api.getEdgePlaybackDownloadUrl({siteId, deviceId, recordId}).then(
        res => {
          resolve(res.body?.url)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getDeviceEdgePlaybacks: function ({commit, state}, {siteId, deviceId, targetRecordId}) {
    commit(GET_DEVICE_EDGE_PLAYBACK_REQUEST, {siteId})
    // API CALL
    return new Promise((resolve, reject) => {
      api.getDeviceEdgePlaybacks({siteId, deviceId, targetRecordId}).then(
        res => {
          let data = res.body
          commit(GET_DEVICE_EDGE_PLAYBACK_SUCCESS, {data, deviceId})
          resolve(state.deviceEdgePlaybacks[deviceId])
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_DEVICE_EDGE_PLAYBACK_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
}

// mutations
const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [INIT_DEVICE_EVENTS]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [GET_SITE_EDGE_PLAYBACK_REQUEST]: function (state) {
    state.status.getSiteEdgePlaybacks = "requested"
  },
  [GET_SITE_EDGE_PLAYBACK_SUCCESS]: function (state, { data }) {
    if (!data?.length) return
  
    let siteEdgePlaybacksTemp = data.map((clip) => {
      return parser.parseSiteEdgePlaybackData(clip)
    })
  
    // created 기준으로 최신순 정렬 (내림차순)
    siteEdgePlaybacksTemp.sort((a, b) => new Date(b.created) - new Date(a.created))
  
    state.siteEdgePlaybacks = siteEdgePlaybacksTemp
    state.status.getSiteEdgePlaybacks = "successful"
  },
  [GET_SITE_EDGE_PLAYBACK_FAILURE]: function (state) {
    state.status.getSiteEdgePlaybacks = "failed"
  },
  [GET_DEVICE_EDGE_PLAYBACK_REQUEST]: function (state) {
    state.status.getDeviceEdgePlaybacks = "requested"
  },
  [GET_DEVICE_EDGE_PLAYBACK_SUCCESS]: function (state, { data, deviceId }) {
    if (!data?.length) return
  
    const deviceEdgePlaybacksTemp = data
      .map((clip) => parser.parseDeviceEdgePlaybackData(clip))
      .sort((a, b) => new Date(b.created) - new Date(a.created)) // 최신순 정렬
  
    Vue.set(state.deviceEdgePlaybacks, deviceId, deviceEdgePlaybacksTemp)
    state.status.getDeviceEdgePlaybacks = "successful"
  },
  [GET_DEVICE_EDGE_PLAYBACK_FAILURE]: function (state) {
    state.status.getDeviceEdgePlaybacks = "failed"
  },
}

export default {
  state,
  getters,
  actions,
  mutations
}